import {
  loginUser,
  validateEmail,
  pushAnalyticsError,
  pushDataLayerEvent,
  buildVerificationHrefForNext,
  resolveSafeNextPath,
  withLocale,
} from "@/helpers";
import { setFieldError, setLoading } from "@/stores";
import { useForm } from "@/hooks";
import { useLocale } from "@/i18n";
import { useSearchParams } from "react-router-dom";

/**
 * useLoginForm Custom Hook
 *
 * Manages login form state and submission logic.
 * Handles validation, API calls, error handling, and navigation.
 */
export const useLoginForm = () => {
  // Get form state from global store
  const { values, loading } = useForm();
  const locale = useLocale();
  const [searchParams] = useSearchParams();
  const nextParam = searchParams.get("next");

  /**
   * Validates login form inputs
   * @returns validation result with error flags
   */
  const validateForm = () => {
    const email = String(values.email ?? "");
    const password = String(values.password ?? "");
    const emailError = !validateEmail(email);
    const passwordError = password.length === 0;

    return {
      emailError,
      passwordError,
      isValid: !emailError && !passwordError,
    };
  };

  /**
   * Handles login API response
   * @param response - API response object
   * @param method - login method ('manual' or 'google')
   */
  const handleLoginResponse = (response: any, method: string = "manual") => {
    switch (response.code) {
      case "logged_in":
        // Push login_success event
        pushDataLayerEvent({
          event: "login_success",
          method: method,
        });
        const { onboardingCompleted, role } = response.data;
        // Demo role / completed verification → safe next or home. Else verification.
        // Access is still enforced by API auth middleware; this is UX only.
        if (role === "demo" || onboardingCompleted) {
          const fallback = withLocale(locale, "/");
          window.location.href = resolveSafeNextPath(nextParam, fallback, locale);
        } else {
          window.location.href = buildVerificationHrefForNext(locale, nextParam);
        }
        break;

      case "invalid_credentials":
        pushAnalyticsError({
          event: "login_error",
          errorCode: "invalid_credentials",
          errorMessage: "Invalid credentials",
          errorStep: "credentials",
          method: "email",
        });
        setFieldError("email", "Invalid credentials");
        setFieldError("password", "Invalid credentials");
        setLoading(false);
        break;

      default:
        pushAnalyticsError({
          event: "login_error",
          errorCode: response.code || "server_error",
          errorMessage: "Something went wrong",
          errorStep: "credentials",
          method: "email",
        });
        setFieldError("password", "Something went wrong!");
        setLoading(false);
        break;
    }
  };

  /**
   * Submits login form with validation and error handling
   */
  const submitLogin = async () => {
    // Push login_start event for manual login
    pushDataLayerEvent({
      event: "login_start",
      method: "manual",
    });

    setLoading(true);

    // Validate form inputs
    const validation = validateForm();

    if (!validation.isValid) {
      // Push login_error event for validation errors
      const errorFields: string[] = [];
      const errorMessages: string[] = [];

      if (validation.emailError) {
        errorFields.push("email");
        errorMessages.push("Invalid e-mail");
      }
      if (validation.passwordError) {
        errorFields.push("password");
        errorMessages.push("Password required");
      }

      pushAnalyticsError({
        event: "login_error",
        errorCode: "validation",
        errorMessage: errorMessages.join("; ") || "Validation failed",
        errorStep: "credentials",
        method: "email",
      });

      // Set validation errors
      setFieldError("email", validation.emailError ? "Invalid e-mail" : "");
      setFieldError(
        "password",
        validation.passwordError ? "Password required" : ""
      );
      setLoading(false);
      return;
    }

    try {
      const email = String(values.email ?? "");
      const password = String(values.password ?? "");
      // Attempt user login
      const response = await loginUser(email, password);
      handleLoginResponse(response, "manual");
    } catch {
      pushAnalyticsError({
        event: "login_error",
        errorCode: "server_error",
        errorMessage: "Something went wrong",
        errorStep: "credentials",
        method: "email",
      });
      setFieldError("password", "Something went wrong!");
      setLoading(false);
    }
  };

  return {
    submitLogin,
    loading,
    values,
  };
};
